import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Badge,
  Button,
  Card,
  Table,
  OverlayTrigger,
  Tooltip,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faEdit,
  faTrash,
  faPause,
  faPlay,
  faCopy,
  faCheck,
  faCheckDouble,
  faPaperPlane,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import {
  deleteMultipleTickets,
  getListClassicInputTicket,
  updateStatusTicket,
  updateMultipleStatusTickets,
} from "../../../../controller/api/__classic__/ticketStorage";
import { useCookies } from "react-cookie";
import { calculatePage } from "../../../../helper/helper";
import Pagination from "../../../../components/Pagination";
import { Context } from "../Input";
import ImagePreview from "../../ticket/sub-screen/image-preview/ImagePreview";
import EditTicketPopup from "../../ticket-storage/sub-screen/EditTicketPopup";

const groupTickets = (tickets) => {
  if (!tickets || tickets.length === 0)
    return { singleTickets: [], pairGroups: [] };

  const singleTickets = [];
  const pairMap = new Map();

  for (const ticket of tickets) {
    if (ticket.isSingle === false) {
      const key = ticket.last5Numbers;
      if (!pairMap.has(key)) {
        pairMap.set(key, []);
      }
      pairMap.get(key).push(ticket);
    } else {
      singleTickets.push(ticket);
    }
  }

  const pairGroups = Array.from(pairMap.entries()).map(
    ([last5Numbers, groupedTickets]) => ({
      last5Numbers,
      tickets: groupedTickets,
      representative: groupedTickets[0],
      totalTickets: groupedTickets.length,
    }),
  );

  pairGroups.sort((a, b) => b.last5Numbers.localeCompare(a.last5Numbers));

  return { singleTickets, pairGroups };
};

const getImageSrc = (img) => {
  if (!img) return null;
  return typeof img === "string" && img.startsWith("http")
    ? img
    : `data:image/jpeg;base64,${img}`;
};

const getTicketCode = (ticket) =>
  ticket?.ticketCode || ticket?.code || ticket?.id || "-";

const getTicketAmount = (ticket) => {
  const value = ticket?.inputAmount ?? ticket?.amount ?? 0;
  return Number(value) || 0;
};

const TicketCodeCell = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(code));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center gap-2">
      <span className="badge bg-light text-dark border fw-normal font-monospace">
        {String(code)}
      </span>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{copied ? "Đã sao chép!" : "Sao chép"}</Tooltip>}
      >
        <Button
          variant="light"
          size="sm"
          onClick={handleCopy}
          className="btn-icon d-inline-flex align-items-center justify-content-center border"
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "6px",
            transition: "all 0.2s",
          }}
        >
          <FontAwesomeIcon
            icon={copied ? faCheck : faCopy}
            className={copied ? "text-success" : "text-muted"}
            fixedWidth
          />
        </Button>
      </OverlayTrigger>
    </div>
  );
};

const ChannelCell = ({ ticket }) => {
  const displayChannel = ticket?.channelName || ticket?.channel || "-";
  const resultDate = ticket?.resultDate || "-";

  return (
    <div className="text-start">
      <div className="fw-bold text-dark">{displayChannel}</div>
      <div className="small text-muted">{resultDate}</div>
    </div>
  );
};

const NumbersCell = ({ value, isPairGroup }) => {
  if (!value) return <span className="text-muted small">-</span>;

  const display = isPairGroup
    ? `* ${(String(value) || "").split("").join(" ")}`
    : (String(value) || "").split("").join(" ");

  return (
    <span
      className={`fw-bold font-monospace ${isPairGroup ? "text-danger" : "text-primary"}`}
      style={{ letterSpacing: "2px", fontSize: "1.1rem" }}
    >
      {display}
    </span>
  );
};

const ImageCell = ({ images, title }) => {
  const [showImagePreview, setShowImagePreview] = useState(false);
  const imageSrc = getImageSrc(images);

  return (
    <>
      {imageSrc ? (
        <div
          className="rounded border overflow-hidden d-inline-block shadow-sm"
          style={{ width: "60px", height: "60px", cursor: "pointer" }}
          onClick={() => setShowImagePreview(true)}
        >
          <img
            src={imageSrc}
            alt="Ticket"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
      ) : (
        <span className="text-muted small">-</span>
      )}
      <ImagePreview
        show={showImagePreview}
        onHide={() => setShowImagePreview(false)}
        image={images}
        title={title}
      />
    </>
  );
};

const ActionButton = ({ icon, variant, onClick, tooltip, className = "" }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
    <Button
      variant={variant}
      size="sm"
      onClick={onClick}
      className={`btn-icon d-inline-flex align-items-center justify-content-center ${className}`}
      style={{ width: "36px", height: "36px", borderRadius: "8px" }}
    >
      <FontAwesomeIcon icon={icon} fixedWidth />
    </Button>
  </OverlayTrigger>
);

const ActionCell = ({ ticket, cookies, onEdit, onRefresh }) => {
  // status=0 means pending, status=undefined also treated as pending since this page only shows pending tickets
  const status = ticket?.status;
  const canApprove = status === 0 || status === undefined;
  const canDelete = status === 0 || status === undefined;
  const canEdit = status === 0 || status === 6 || status === undefined;
  const canPause = status === 1;
  const canResume = status === 6;

  return (
    <div className="d-flex justify-content-center gap-2 flex-wrap">
      {canApprove && (
        <ActionButton
          icon={faCheck}
          variant="success"
          onClick={async () => {
            const result = await updateStatusTicket(
              cookies.lottmekong,
              ticket.id,
              1,
            );
            if (result != null) onRefresh && onRefresh();
          }}
          tooltip="Duyệt vé"
          className="shadow-sm"
        />
      )}
      {canEdit && (
        <ActionButton
          icon={faEdit}
          variant="light text-primary"
          onClick={onEdit}
          tooltip="Chỉnh sửa"
          className="border shadow-sm hover-primary"
        />
      )}
      {canPause && (
        <ActionButton
          icon={faPause}
          variant="light text-warning"
          onClick={async () => {
            const result = await updateStatusTicket(
              cookies.lottmekong,
              ticket.id,
              6,
            );
            if (result != null) onRefresh && onRefresh();
          }}
          tooltip="Tạm ngưng"
          className="border shadow-sm hover-warning"
        />
      )}
      {canResume && (
        <ActionButton
          icon={faPlay}
          variant="light text-success"
          onClick={async () => {
            const result = await updateStatusTicket(
              cookies.lottmekong,
              ticket.id,
              1,
            );
            if (result != null) onRefresh && onRefresh();
          }}
          tooltip="Mở bán"
          className="border shadow-sm hover-success"
        />
      )}
      {canDelete && (
        <ActionButton
          icon={faTimes}
          variant="danger"
          onClick={async () => {
            if (window.confirm("Bạn có chắc chắn muốn huỷ vé này?")) {
              const result = await deleteMultipleTickets(cookies.lottmekong, [
                ticket.id,
              ]);
              if (result && result.success) onRefresh && onRefresh();
            }
          }}
          tooltip="Huỷ vé"
          className="shadow-sm"
        />
      )}
    </div>
  );
};

const PendingSingleRow = ({
  rIndex,
  ticket,
  cookies,
  onEdit,
  onRefresh,
  typeLabel,
  typeBadgeVariant,
  showCheckbox = false,
  isSelected = false,
  onSelectChange = null,
}) => {
  const code = getTicketCode(ticket);
  const createdAt = ticket?.createdAt
    ? moment(ticket.createdAt).format("DD/MM HH:mm")
    : "-";
  const createdBy = ticket?.createdByName || "-";

  // Only allow selection for pending tickets (status=0 or undefined)
  const canSelect = ticket?.status === 0 || ticket?.status === undefined;

  return (
    <tr className="align-middle border-bottom">
      {showCheckbox && (
        <td className="text-center" style={{ width: "50px" }}>
          <div className="form-check d-flex justify-content-center">
            <input
              className="form-check-input"
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                if (onSelectChange) {
                  onSelectChange(ticket.id, e.target.checked, ticket);
                }
              }}
              disabled={!canSelect}
              style={{
                cursor: canSelect ? "pointer" : "not-allowed",
                transform: "scale(1.2)",
              }}
            />
          </div>
        </td>
      )}
      <td className="text-center fw-bold text-muted">{rIndex + 1}</td>
      <td className="text-center">
        <TicketCodeCell code={code} />
      </td>
      <td className="text-center">
        <Badge bg={typeBadgeVariant || "info"} className="px-3 py-2 fw-normal">
          {typeLabel || "Vé đơn"}
        </Badge>
      </td>
      <td className="text-center">
        <ChannelCell ticket={ticket} />
      </td>
      <td className="text-center">
        <NumbersCell value={ticket?.numbers} />
      </td>
      <td className="text-center fw-bold text-dark">
        {new Intl.NumberFormat("vi-VN").format(getTicketAmount(ticket))}
      </td>
      <td className="text-center">
        <ImageCell
          images={ticket?.images}
          title={`Vé số ${ticket?.numbers || ""}`}
        />
      </td>
      <td className="text-center text-muted small">{createdBy}</td>
      <td className="text-center text-muted small">{createdAt}</td>
      <td className="text-center">
        <ActionCell
          ticket={ticket}
          cookies={cookies}
          onEdit={onEdit}
          onRefresh={onRefresh}
        />
      </td>
    </tr>
  );
};

const PendingPairGroup = ({
  rIndex,
  pairGroup,
  cookies,
  onEdit,
  onRefresh,
  showCheckbox = false,
  selectedTicketIds = [],
  onSelectChange = null,
}) => {
  const { last5Numbers, representative, tickets } = pairGroup;
  const [expanded, setExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const code = getTicketCode(representative);
  const createdAt = representative?.createdAt
    ? moment(representative.createdAt).format("DD/MM HH:mm")
    : "-";
  const createdBy = representative?.createdByName || "-";

  const totalInputAmount = tickets.reduce(
    (sum, t) => sum + getTicketAmount(t),
    0,
  );

  // Check if all tickets in the pair group can be deleted (status = 0 or undefined)
  const canDeleteGroup = tickets.every(
    (t) => t?.status === 0 || t?.status === undefined,
  );
  const canApproveGroup = canDeleteGroup;

  // Check if all tickets in the pair group can be selected
  const canSelect = canDeleteGroup;

  // Check if all tickets in group are selected
  const allTicketsSelected = tickets.every((t) =>
    selectedTicketIds.includes(t.id),
  );
  const someTicketsSelected =
    tickets.some((t) => selectedTicketIds.includes(t.id)) &&
    !allTicketsSelected;

  const handleGroupCheckboxChange = (e) => {
    if (!onSelectChange) return;

    const isChecked = e.target.checked;
    // Select/deselect all tickets in the group
    tickets.forEach((ticket) => {
      if (ticket?.status === 0 || ticket?.status === undefined) {
        onSelectChange(ticket.id, isChecked, ticket);
      }
    });
  };

  const handleDeletePairGroup = async (e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Bạn có chắc chắn muốn xóa TẤT CẢ ${tickets.length} vé trong nhóm cặp nguyên *${last5Numbers}?`,
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      const ticketIds = tickets.map((t) => t.id);
      const result = await deleteMultipleTickets(cookies.lottmekong, ticketIds);

      if (result && result.success) {
        onRefresh && onRefresh();
        if (result.deletedCount < result.requestedCount) {
          alert(
            `Đã xóa ${result.deletedCount}/${result.requestedCount} vé. Một số vé không thể xóa.`,
          );
        }
      } else {
        alert("Có lỗi xảy ra khi xóa vé.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApprovePairGroup = async (e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Bạn có chắc chắn muốn duyệt TẤT CẢ ${tickets.length} vé trong nhóm cặp nguyên *${last5Numbers}?`,
      )
    ) {
      return;
    }

    setIsApproving(true);
    try {
      const ticketIds = tickets.map((t) => t.id);
      const result = await updateMultipleStatusTickets(
        cookies.lottmekong,
        ticketIds,
        1,
      );

      if (result && result.success) {
        onRefresh && onRefresh();
        if (result.updatedCount < result.requestedCount) {
          alert(
            `Đã duyệt ${result.updatedCount}/${result.requestedCount} vé. Một số vé không thể duyệt.`,
          );
        }
      } else {
        alert("Có lỗi xảy ra khi duyệt vé.");
      }
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <>
      <tr
        className={`align-middle ${expanded ? "bg-light" : "bg-white"} border-bottom`}
        style={{ cursor: "pointer", transition: "all 0.2s" }}
        onClick={() => setExpanded(!expanded)}
      >
        {showCheckbox && (
          <td
            className="text-center"
            style={{ width: "50px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="form-check d-flex justify-content-center">
              <input
                className="form-check-input"
                type="checkbox"
                checked={allTicketsSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someTicketsSelected;
                  }
                }}
                onChange={handleGroupCheckboxChange}
                disabled={!canSelect}
                style={{
                  cursor: canSelect ? "pointer" : "not-allowed",
                  transform: "scale(1.2)",
                }}
              />
            </div>
          </td>
        )}
        <td className="text-center fw-bold text-muted">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none text-dark"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              className="me-2 text-muted"
            />
            {rIndex + 1}
          </Button>
        </td>
        <td className="text-center">
          <TicketCodeCell code={code} />
        </td>
        <td className="text-center">
          <Badge bg="warning" text="dark" className="px-3 py-2 fw-normal">
            Vé cặp nguyên
          </Badge>
          <div className="small text-muted mt-1">{tickets.length} vé</div>
        </td>
        <td className="text-center">
          <ChannelCell ticket={representative} />
        </td>
        <td className="text-center">
          <NumbersCell value={last5Numbers} isPairGroup />
        </td>
        <td className="text-center fw-bold text-dark">
          {new Intl.NumberFormat("vi-VN").format(totalInputAmount)}
        </td>
        <td className="text-center">
          <ImageCell
            images={representative?.images}
            title={`Vé cặp *${last5Numbers}`}
          />
        </td>
        <td className="text-center text-muted small">{createdBy}</td>
        <td className="text-center text-muted small">{createdAt}</td>
        <td className="text-center">
          <div className="d-flex justify-content-center gap-2 flex-wrap">
            {canApproveGroup && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {isApproving
                      ? "Đang duyệt..."
                      : `Duyệt tất cả ${tickets.length} vé`}
                  </Tooltip>
                }
              >
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleApprovePairGroup}
                  disabled={isApproving || isDeleting}
                  className="btn-icon d-inline-flex align-items-center justify-content-center shadow-sm"
                  style={{ width: "36px", height: "36px", borderRadius: "8px" }}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    fixedWidth
                    spin={isApproving}
                  />
                </Button>
              </OverlayTrigger>
            )}
            {canDeleteGroup && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {isDeleting
                      ? "Đang huỷ..."
                      : `Huỷ tất cả ${tickets.length} vé`}
                  </Tooltip>
                }
              >
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeletePairGroup}
                  disabled={isDeleting || isApproving}
                  className="btn-icon d-inline-flex align-items-center justify-content-center shadow-sm"
                  style={{ width: "36px", height: "36px", borderRadius: "8px" }}
                >
                  <FontAwesomeIcon
                    icon={faTimes}
                    fixedWidth
                    spin={isDeleting}
                  />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </td>
      </tr>

      {expanded &&
        tickets.map((ticket, idx) => (
          <PendingSingleRow
            key={ticket.id}
            rIndex={idx}
            ticket={ticket}
            cookies={cookies}
            onEdit={() => onEdit(ticket)}
            onRefresh={onRefresh}
            typeLabel="Con"
            typeBadgeVariant="secondary"
            showCheckbox={showCheckbox}
            isSelected={selectedTicketIds.includes(ticket.id)}
            onSelectChange={onSelectChange}
          />
        ))}
    </>
  );
};

const _Table = ({ region }) => {
  const { state, dispatch } = useContext(Context);
  const [cookies] = useCookies(["lottmekong"]);
  const [data, setData] = useState({});
  const [editTicketPopupShow, setEditTicketPopupShow] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Multi-select state
  const [selectedTickets, setSelectedTickets] = useState(new Map()); // Map<id, ticket>

  const { singleTickets, pairGroups } = useMemo(
    () => groupTickets(data.list),
    [data.list],
  );

  // Get all selectable tickets (only pending tickets: status=0 or undefined)
  const allSelectableTickets = useMemo(() => {
    const fromPairs = pairGroups.flatMap((g) =>
      g.tickets.filter((t) => t?.status === 0 || t?.status === undefined),
    );
    const fromSingles = singleTickets.filter(
      (t) => t?.status === 0 || t?.status === undefined,
    );
    return [...fromPairs, ...fromSingles];
  }, [pairGroups, singleTickets]);

  const selectedTicketIds = useMemo(
    () => Array.from(selectedTickets.keys()),
    [selectedTickets],
  );

  const getAllFunc = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const response = await getListClassicInputTicket(
        cookies.lottmekong,
        {
          channel: state.filter.channel,
          region: region,
          numbers: state.filter.numbers,
          ticketCode: state.filter.ticketCode,
          ticketType: state.filter.ticketType || "all",
          fromDate: state.filter.fromDate,
          toDate: state.filter.toDate,
        },
        state.filter.page,
      );

      if (response) {
        const res = {
          list: response.data,
          total: response.total,
          totalPage: calculatePage(response.total),
        };
        setData(res || {});
      }
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [
    cookies.lottmekong,
    dispatch,
    state.filter.channel,
    state.filter.numbers,
    state.filter.ticketCode,
    state.filter.ticketType,
    state.filter.fromDate,
    state.filter.toDate,
    state.filter.page,
    region,
  ]);

  useEffect(() => {
    getAllFunc();
  }, [getAllFunc, state.filterPressCount]);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedTickets(new Map());
  }, [data.list]);

  const handleSelectTicket = (ticketId, isSelected, ticketData) => {
    setSelectedTickets((prev) => {
      const next = new Map(prev);
      if (isSelected) {
        next.set(ticketId, ticketData);
      } else {
        next.delete(ticketId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedTickets.size === allSelectableTickets.length) {
      // Deselect all
      setSelectedTickets(new Map());
    } else {
      // Select all
      const newMap = new Map();
      allSelectableTickets.forEach((t) => newMap.set(t.id, t));
      setSelectedTickets(newMap);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTickets.size === 0) return;

    if (
      !window.confirm(
        `Bạn có chắc chắn muốn xóa ${selectedTickets.size} vé đã chọn?`,
      )
    ) {
      return;
    }

    try {
      const ticketIds = Array.from(selectedTickets.keys());
      const result = await deleteMultipleTickets(cookies.lottmekong, ticketIds);

      if (result && result.success) {
        setSelectedTickets(new Map());
        getAllFunc();
        if (result.deletedCount < result.requestedCount) {
          alert(
            `Đã xóa ${result.deletedCount}/${result.requestedCount} vé. Một số vé không thể xóa.`,
          );
        }
      } else {
        alert("Có lỗi xảy ra khi xóa vé.");
      }
    } catch (error) {
      console.error("Error deleting tickets:", error);
      alert("Có lỗi xảy ra khi xóa vé.");
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedTickets.size === 0) return;

    if (
      !window.confirm(
        `Bạn có chắc chắn muốn duyệt ${selectedTickets.size} vé đã chọn?`,
      )
    ) {
      return;
    }

    try {
      // Get all selected ticket IDs
      const ticketIds = Array.from(selectedTickets.keys());

      // Call API to update status to 1 (approved/for sale)
      const result = await updateMultipleStatusTickets(
        cookies.lottmekong,
        ticketIds,
        1, // status = 1 means approved/for sale
      );

      if (result && result.success) {
        setSelectedTickets(new Map());
        getAllFunc();
        if (result.updatedCount < result.requestedCount) {
          alert(
            `Đã duyệt ${result.updatedCount}/${result.requestedCount} vé. Một số vé không thể duyệt.`,
          );
        } else {
          alert(`Duyệt ${result.updatedCount} vé thành công!`);
        }
      } else {
        alert("Có lỗi xảy ra khi duyệt vé.");
      }
    } catch (error) {
      console.error("Error submitting tickets:", error);
      alert("Có lỗi xảy ra khi duyệt vé.");
    }
  };

  const handleEdit = (ticket) => {
    setSelectedTicket(ticket);
    setEditTicketPopupShow(true);
  };

  const allSelected =
    allSelectableTickets.length > 0 &&
    selectedTickets.size === allSelectableTickets.length;

  return (
    <>
      <Card border="0" className="shadow-sm rounded-3">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table className="table-centered table-nowrap mb-0" hover>
              <thead className="bg-light text-muted small text-uppercase">
                <tr>
                  <th
                    className="border-0 text-center p-3"
                    scope="col"
                    style={{ width: "50px" }}
                  >
                    <div className="form-check d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        style={{ cursor: "pointer", transform: "scale(1.2)" }}
                      />
                    </div>
                  </th>
                  <th className="border-0 text-center p-3" scope="col">
                    #
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Mã vé
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Loại vé
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Đài / Kỳ quay
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Dãy số
                  </th>
                  <th className="border-0 text-center" scope="col">
                    SL Nhập
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Hình ảnh
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Người nhập
                  </th>
                  <th className="border-0 text-center" scope="col">
                    Ngày nhập
                  </th>
                  <th className="border-0 rounded-end text-center" scope="col">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="border-0">
                {pairGroups.map((group, index) => (
                  <PendingPairGroup
                    rIndex={index}
                    key={`pair-group-${group.last5Numbers}`}
                    pairGroup={group}
                    cookies={cookies}
                    onEdit={handleEdit}
                    onRefresh={getAllFunc}
                    showCheckbox={true}
                    selectedTicketIds={selectedTicketIds}
                    onSelectChange={handleSelectTicket}
                  />
                ))}
                {singleTickets.map((ticket, index) => (
                  <PendingSingleRow
                    rIndex={pairGroups.length + index}
                    key={`pending-single-${ticket.id}`}
                    ticket={ticket}
                    cookies={cookies}
                    onEdit={() => handleEdit(ticket)}
                    onRefresh={getAllFunc}
                    showCheckbox={true}
                    isSelected={selectedTickets.has(ticket.id)}
                    onSelectChange={handleSelectTicket}
                  />
                ))}
                {(!data.list || data.list.length === 0) && (
                  <tr>
                    <td colSpan="11" className="text-center py-5">
                      <div className="d-flex flex-column align-items-center justify-content-center text-muted">
                        <span className="fs-1 display-1 opacity-25">∅</span>
                        <span className="mt-2 fs-6">Chưa có dữ liệu</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer className="px-4 py-3 border-0 bg-white d-flex flex-column flex-lg-row align-items-center justify-content-between rounded-bottom">
          <div className="fw-normal small mt-1 mt-lg-0 text-muted">
            Hiển thị <b>{data.list?.length || 0}</b> trong tổng{" "}
            <b>{data.total || 0}</b> vé
          </div>
          <Pagination
            totalPage={data.totalPage || 0}
            currentPage={state.filter.page || 0}
            onPageChange={(i) => dispatch({ type: "UPDATE_PAGE", payload: i })}
          />
        </Card.Footer>
      </Card>

      <EditTicketPopup
        show={editTicketPopupShow}
        onHide={() => {
          setSelectedTicket(null);
          setEditTicketPopupShow(false);
        }}
        ticketData={selectedTicket}
        cookies={cookies}
        onRefresh={getAllFunc}
      />

      {/* Sticky action bar when tickets are selected */}
      {selectedTickets.size > 0 && (
        <div
          className="position-fixed bottom-0 start-0 end-0 bg-white shadow-lg border-top border-2 border-primary py-3 px-4"
          style={{
            zIndex: 1000,
            animation: "slideUp 0.3s ease-out",
          }}
        >
          <div className="container-fluid d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={faCheckDouble}
                className="text-primary fs-4"
              />
              <div>
                <div className="fw-bold text-primary fs-5">
                  Đã chọn {selectedTickets.size} vé
                </div>
                <div className="text-muted small">
                  Nhấn nút bên phải để thực hiện thao tác với các vé đã chọn
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={() => setSelectedTickets(new Map())}
              >
                Bỏ chọn
              </Button>
              <Button
                variant="danger"
                size="lg"
                onClick={handleBulkDelete}
                className="px-4"
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Xóa {selectedTickets.size} vé
              </Button>
              <Button
                variant="success"
                size="lg"
                onClick={handleBulkSubmit}
                className="px-4"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Duyệt {selectedTickets.size} vé
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default React.memo(_Table);
